import { z } from 'zod';
/**
 * Контракты эндпоинтов `modules/items` (docs/api-design.md §4.7,
 * docs/data-model.md §3.5, docs/REQUIREMENTS.md FR-4/FR-5).
 *
 * M5: CRUD записей, inline-edit. Фильтры/представления — в M6.
 *
 * Значения полей передаются как плоский объект {slug: value}:
 *   text/textarea/markdown/url → string | null
 *   number                    → number | null
 *   date                      → ISO-date string ("2026-04-15") | null
 *   boolean                   → boolean | null
 *   select                    → string (option value) | null
 *   multi_select              → string[] | null
 *   reference                 → string | string[] | null (entryId или entryIds)
 */
// ─── ItemRow DTO (ответ на запрос записей) ────────────────────────────────────
export const ItemRowSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    createdById: z.string().uuid(),
    /** displayName/login пользователя, создавшего запись (если резолвится). */
    createdByDisplayName: z.string().nullable().optional(),
    // Читаемый ключ записи вида "PROJ-123"; уникален в рамках пространства; null для legacy-записей до миграции.
    displayKey: z.string().nullable().optional(),
    jiraIssueKey: z.string().nullable(),
    jiraStatus: z.string().nullable(),
    jiraSyncedAt: z.string().nullable(),
    hermesStatus: z.string().nullable().optional(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // Плоская карта значений полей: { slug → typed value }
    fields: z.record(z.unknown()),
});
/**
 * Значение link_aggregate-колонки для одной записи. См. M11.6:
 * - mode=list  → массив краткой инфы по связанным записям;
 * - mode=count → число (n связей);
 * - mode=sum / avg → число (агрегат `valueFieldSlug` по связанным записям; null,
 *   если связей нет).
 */
export const LinkedAggregateListEntrySchema = z.object({
    linkId: z.string().uuid().optional(),
    itemId: z.string().uuid(),
    spaceId: z.string().uuid(),
    itemTypeId: z.string().uuid(),
    displayKey: z.string().nullable(),
    title: z.string().nullable(),
});
export const LinkedAggregateValueSchema = z.union([
    z.number().nullable(),
    z.array(LinkedAggregateListEntrySchema),
]);
export const ViewComputedValueSchema = z.union([
    z.number(),
    z.string(),
    z.null(),
]);
// Список с пагинацией
export const ItemListResponseSchema = z.object({
    items: z.array(ItemRowSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int().nullable(),
    // Резолвленные reference-значения: entryId → display string
    resolvedRefs: z.record(z.string()).optional(),
    /**
     * M11.6: для каждой записи текущей страницы — карта `columnKey → value`,
     * где columnKey — стабильный key из ColumnConfig.kind='link_aggregate',
     * value — число (count/sum/avg), null (sum/avg без связей) или
     * массив LinkedAggregateListEntry (mode='list'). Поле опционально:
     * присутствует только если у view есть link_aggregate-колонки.
     */
    linkedAggregates: z
        .record(z.record(LinkedAggregateValueSchema))
        .optional(),
    /**
     * View-only computed columns: для каждой записи текущей страницы — карта
     * `columnKey → value`, где value — число, строка или null. Поле опционально:
     * присутствует только если у view есть колонки kind='view_computed'.
     */
    viewComputed: z
        .record(z.record(ViewComputedValueSchema))
        .optional(),
});
// ─── POST /api/spaces/:id/items ──────────────────────────────────────────────
export const CreateItemRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    // Начальные значения полей (опционально — только те поля, которые надо заполнить)
    fields: z.record(z.unknown()).optional().default({}),
});
// ─── PATCH /api/spaces/:id/items/:itemId ─────────────────────────────────────
// Partial update одного или нескольких полей одним запросом.
// Один вызов → одна транзакция → пересчёт computed (M7) → один audit entry (M9).
export const PatchItemRequestSchema = z.object({
    fields: z.record(z.unknown()),
});
// ─── POST /api/spaces/:id/items/reorder ──────────────────────────────────────
// Ручной порядок карточек (канбан). Общий для всех пользователей — пишется в
// глобальный `items.sort_order`. `orderedIds` — желаемый порядок некоторого
// подмножества записей (обычно все карточки одной колонки канбана). Сервер
// переставляет их по уже занятым этими же записями слотам sort_order, не трогая
// порядок остальных записей. См. docs/api-design.md §4.7.
export const ReorderItemsRequestSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// ─── PATCH /api/spaces/:id/items/:itemId/type ────────────────────────────────
// Смена типа записи (например «командная цель» → «личная цель») с сохранением
// самой записи, комментариев, истории, Jira-связки и cross-links. Значения
// полей переносятся по совпадению slug (и совместимости типа); несовместимые
// и отсутствующие в новом типе поля теряются — список возвращается в ответе.
export const ChangeItemTypeRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
});
// Сводка переноса значений: какие slug'и перенеслись, какие потеряны.
export const ChangeItemTypeSummarySchema = z.object({
    carried: z.array(z.string()),
    dropped: z.array(z.string()),
});
// ─── POST /api/spaces/:id/items/query ────────────────────────────────────────
// M5: упрощённый вариант без фильтров/сортировок (они в M6).
// itemTypeId обязателен; cursor/limit для пагинации.
export const QueryItemsRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(500).optional().default(100),
});
// ─── M11: Cross-links ───────────────────────────────────────────────────────
export const CopyToSpaceRequestSchema = z.object({
    targetSpaceId: z.string().uuid(),
    targetItemTypeId: z.string().uuid(),
    linkFieldSlug: z.string().optional(), // slug of cross_link field to auto-fill on source
});
// ─── Замена текста по маске (дублирование + массовое редактирование) ─────────
// Для каждого текстового поля задаётся подстрока `search`, которая заменяется на
// `replace` (все вхождения). Применяется к строковому значению поля: при
// дублировании — к значению из источника, при bulk-update — к текущему значению
// каждой записи. Нестроковые/пустые значения пропускаются. См. todos #4.
export const FieldReplacementSchema = z.object({
    fieldSlug: z.string().min(1),
    search: z.string().min(1),
    replace: z.string(),
});
// ─── M10: Bulk operations ───────────────────────────────────────────────────
export const BulkUpdateRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
    fields: z.record(z.unknown()),
    replacements: z.array(FieldReplacementSchema).optional(),
});
export const BulkUpdateResponseSchema = z.object({
    updated: z.number().int(),
});
export const BulkDeleteRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(1000),
});
export const BulkDeleteResponseSchema = z.object({
    deleted: z.number().int(),
});
// ─── Item duplication (single + bulk, same space) ───────────────────────────
// `overrides` — частичный набор {slug: value}, который переопределяет значения
// исходной записи при дублировании. Поля, которых нет в overrides (или undefined),
// копируются из исходных записей как есть. Поля со значением `null` интерпретируются
// как «очистить». См. SpacePage → DuplicateModal (FR-4 / FR-5).
export const DuplicateItemRequestSchema = z.object({
    overrides: z.record(z.unknown()).optional(),
    replacements: z.array(FieldReplacementSchema).optional(),
});
export const BulkDuplicateRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(500),
    overrides: z.record(z.unknown()).optional(),
    replacements: z.array(FieldReplacementSchema).optional(),
});
export const BulkDuplicateResponseSchema = z.object({
    duplicated: z.number().int(),
    itemIds: z.array(z.string().uuid()),
});
export const CsvImportRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    mapping: z.record(z.string()), // CSV column name → field slug
});
